import { useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HomeIcon,
  CalendarDaysIcon,
  WrenchScrewdriverIcon,
  BookOpenIcon,
  Bars3BottomLeftIcon,
  SunIcon,
  MoonIcon,
  ClipboardDocumentListIcon,
  HeartIcon,
  BookmarkSquareIcon,
  PencilSquareIcon,
  MapPinIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import { useThemeStore } from '../../stores/themeStore';

type SubmenuId = 'daily' | 'tools' | 'more' | null;

const dailyLinks = [
  { label: 'Morning Check-in', href: '/checkin',        icon: SunIcon },
  { label: 'Nightly Review',   href: '/nightly-review', icon: MoonIcon },
  { label: 'Activity Log',     href: '/activity-log',   icon: ClipboardDocumentListIcon },
  { label: 'Gratitude',        href: '/checkin',        icon: HeartIcon },
];

const toolLinks = [
  { label: 'Step Work',         href: '/steps',              icon: BookmarkSquareIcon },
  { label: 'Journal',           href: '/journal',            icon: PencilSquareIcon },
  { label: 'Find Meetings',     href: '/meetings',           icon: MapPinIcon },
  { label: 'Sponsor Dashboard', href: '/sponsor-dashboard',  icon: UserGroupIcon },
];

interface SubmenuProps {
  title: string;
  links: { label: string; href: string; icon: typeof SunIcon }[];
  onClose: () => void;
}

function Submenu({ title, links, onClose }: SubmenuProps) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/20 z-40"
      />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] as const }}
        className="fixed bottom-20 left-4 right-4 z-50 bg-white dark:bg-surface-800 rounded-xl shadow-xl border border-surface-200 dark:border-surface-700 overflow-hidden"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200 dark:border-surface-700">
          <h3 className="font-semibold text-surface-800 dark:text-white">{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close menu"
            className="p-1.5 rounded-lg text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-2">
          {links.map(item => (
            <Link
              key={item.href}
              to={item.href}
              onClick={onClose}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-700/50 transition-colors"
            >
              <item.icon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              <span className="text-sm font-medium text-surface-800 dark:text-white">{item.label}</span>
            </Link>
          ))}
        </div>
      </motion.div>
    </>
  );
}

function MoreSubmenu({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { resolvedTheme, setTheme } = useThemeStore();
  const displayName = user?.email?.split('@')[0] ?? 'Friend';

  const handleLogout = async () => {
    onClose();
    try {
      await logout();
      navigate('/login');
    } catch {
      // authStore clears local state even on error
      navigate('/login');
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/20 z-40"
      />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] as const }}
        className="fixed bottom-20 left-4 right-4 z-50 bg-white dark:bg-surface-800 rounded-xl shadow-xl border border-surface-200 dark:border-surface-700 overflow-hidden"
      >
        {/* Profile header */}
        <div className="px-4 py-3 border-b border-surface-200 dark:border-surface-700">
          <p className="text-sm font-semibold text-navy-800 dark:text-surface-100">{displayName}</p>
          <p className="text-xs text-surface-500 dark:text-surface-400 truncate">{user?.email}</p>
        </div>
        <div className="p-2">
          <Link
            to="/settings"
            onClick={onClose}
            className="flex items-center gap-3 p-3 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-700/50 transition-colors"
          >
            <Cog6ToothIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            <span className="text-sm font-medium text-surface-800 dark:text-white">Settings</span>
          </Link>
          <button
            onClick={() => { setTheme(resolvedTheme === 'dark' ? 'light' : 'dark'); onClose(); }}
            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-700/50 transition-colors"
          >
            {resolvedTheme === 'dark' ? (
              <SunIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            ) : (
              <MoonIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            )}
            <span className="text-sm font-medium text-surface-800 dark:text-white">
              {resolvedTheme === 'dark' ? 'Light mode' : 'Dark mode'}
            </span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-700/50 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5 text-danger-500" />
            <span className="text-sm font-medium text-danger-600 dark:text-danger-400">Sign Out</span>
          </button>
        </div>
      </motion.div>
    </>
  );
}

export function BottomNav() {
  const [openSubmenu, setOpenSubmenu] = useState<SubmenuId>(null);
  const close = () => setOpenSubmenu(null);

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/90 dark:bg-surface-900/90 backdrop-blur-lg border-t border-surface-200 dark:border-surface-800"
        aria-label="Mobile navigation"
      >
        <div className="flex items-center justify-around h-16 px-2">
          {/* Home — direct */}
          <NavLink
            to="/dashboard"
            aria-label="Home"
            className={({ isActive }) =>
              `flex flex-col items-center justify-center flex-1 h-full px-2 transition-colors duration-150
               ${isActive ? 'text-teal-600 dark:text-teal-400' : 'text-surface-500 dark:text-surface-400'}`
            }
          >
            <HomeIcon className="w-6 h-6" />
            <span className="text-xs mt-1 font-medium">Home</span>
          </NavLink>

          {/* Daily — submenu */}
          <button
            onClick={() => setOpenSubmenu(v => v === 'daily' ? null : 'daily')}
            aria-label="Daily practice menu"
            aria-expanded={openSubmenu === 'daily'}
            aria-haspopup="dialog"
            className={`flex flex-col items-center justify-center flex-1 h-full px-2 transition-colors duration-150
              ${openSubmenu === 'daily' ? 'text-teal-600 dark:text-teal-400' : 'text-surface-500 dark:text-surface-400'}`}
          >
            <CalendarDaysIcon className="w-6 h-6" />
            <span className="text-xs mt-1 font-medium">Daily</span>
          </button>

          {/* Tools — submenu */}
          <button
            onClick={() => setOpenSubmenu(v => v === 'tools' ? null : 'tools')}
            aria-label="Tools menu"
            aria-expanded={openSubmenu === 'tools'}
            aria-haspopup="dialog"
            className={`flex flex-col items-center justify-center flex-1 h-full px-2 transition-colors duration-150
              ${openSubmenu === 'tools' ? 'text-teal-600 dark:text-teal-400' : 'text-surface-500 dark:text-surface-400'}`}
          >
            <WrenchScrewdriverIcon className="w-6 h-6" />
            <span className="text-xs mt-1 font-medium">Tools</span>
          </button>

          {/* Read — direct */}
          <NavLink
            to="/read"
            aria-label="Read"
            className={({ isActive }) =>
              `flex flex-col items-center justify-center flex-1 h-full px-2 transition-colors duration-150
               ${isActive ? 'text-teal-600 dark:text-teal-400' : 'text-surface-500 dark:text-surface-400'}`
            }
          >
            <BookOpenIcon className="w-6 h-6" />
            <span className="text-xs mt-1 font-medium">Read</span>
          </NavLink>

          {/* More — submenu */}
          <button
            onClick={() => setOpenSubmenu(v => v === 'more' ? null : 'more')}
            aria-label="More options"
            aria-expanded={openSubmenu === 'more'}
            aria-haspopup="dialog"
            className={`flex flex-col items-center justify-center flex-1 h-full px-2 transition-colors duration-150
              ${openSubmenu === 'more' ? 'text-teal-600 dark:text-teal-400' : 'text-surface-500 dark:text-surface-400'}`}
          >
            <Bars3BottomLeftIcon className="w-6 h-6" />
            <span className="text-xs mt-1 font-medium">More</span>
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {openSubmenu === 'daily' && (
          <Submenu title="Daily Practice" links={dailyLinks} onClose={close} />
        )}
        {openSubmenu === 'tools' && (
          <Submenu title="Tools & Community" links={toolLinks} onClose={close} />
        )}
        {openSubmenu === 'more' && (
          <MoreSubmenu onClose={close} />
        )}
      </AnimatePresence>
    </>
  );
}

export default BottomNav;
